import { datadogRum, RumEvent, RumEventDomainContext } from '@datadog/browser-rum';
import { datadogVersion } from '../config/settings/app.settings';
import { location } from '../adapters/';

function shouldEnableTelemetry(): boolean {
  const env: string = import.meta.env.VITE_NODE_ENV ?? 'development';
  return env === 'production' || env === 'staging' || env === 'qa';
}

function filterInvalidErrors(event: RumEvent, context: RumEventDomainContext): boolean {
  // Hide Outlook's Cef Scraper's errors https://trackjs.com/javascript-errors/object-not-found-matching-id-methodname-paramcount/
  if (event.type === 'error' && event.error.message.includes('Object Not Found Matching Id')) {
    return false;
  }

  // After a deployment, the files for dynamic routing can change. Which can
  // cause this error until a refresh has happened
  // If we detect one of these errors, don't log it, but refresh the page.
  if (event.type === 'error' && event.error.message.includes('Failed to fetch dynamically imported module')) {
    location.reload();
    return false;
  }

  // Don't log random network failures, as they are almost always just
  // caused by the customers internet connection
  if (event.type === 'error' && event.error.message === 'Failed to fetch') {
    return false;
  }

  // Ignore any errors thrown by web extensions, as they are outside of our
  // control
  if (event.type === 'error' && event.error.stack) {
    const stackTrace = event.error.stack;
    // Check if the stack trace contains common extension-related paths
    if (stackTrace.includes('chrome-extension://') || stackTrace.includes('moz-extension://')) {
      return false;
    }
  }

  return true;
}

function initTelemetry() {
  if (!shouldEnableTelemetry()) {
    return;
  }
  datadogRum.init({
    applicationId: '6edc5825-ab5f-4644-9ba4-1ffc3af3dde4',
    clientToken: 'pub330f02c4e84d5e473bcc0053f6f82ee6',
    site: 'us5.datadoghq.com',
    service: 'pulse-web',
    env: import.meta.env.VITE_NODE_ENV,
    version: datadogVersion,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    // beforeSend is called before sending each event to Datadog, so we can filter out unwanted errors
    beforeSend: filterInvalidErrors,
  });

  datadogRum.startSessionReplayRecording();
}

function setUser(user: { companyId: string; email: string }) {
  if (!shouldEnableTelemetry()) {
    return;
  }
  if (user) {
    datadogRum.setUser(user);
  }
}
function reportError({ error, context }: { error: Error; context: Record<string, unknown> }): void {
  if (!shouldEnableTelemetry()) {
    return;
  }
  datadogRum.addError(error, context);
}

export { initTelemetry, setUser, reportError, filterInvalidErrors };
